/**
 * useBoardingPassGame.ts — Shared game logic for the Security Checkpoint.
 *
 * Contains all state, handlers, and effects that are identical between the
 * desktop component (BoardingPassClearance) and the mobile CAPTCHA component
 * (BoardingPassClearanceMobile). Neither component declares game state
 * locally — they only call this hook and render their own JSX.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import type React from 'react';
import { useTurnstile } from '@/hooks/useTurnstile';
import { verifyTurnstileToken, TurnstileVerificationError } from '@/lib/turnstile';
import { ALL_ITEMS, CORRECT_ITEMS } from './boardingPassItems';

// ─── Types ───────────────────────────────────────────────────────────────────

export type Phase = 'puzzle' | 'success' | 'verifying';

export interface GameProps {
  onVerified: () => void;
  onError: (msg: string) => void;
}

export interface GameState {
  phase: Phase;
  count: number;
  packedDisplay: Set<string>;
  shakenItem: string | null;
  wrongMsg: string | null;
  isDragOver: boolean;
  backpackGlow: boolean;
  draggedId: string | null;
  selectedId: string | null;
  dropZoneRef: React.RefObject<HTMLDivElement | null>;
  handlePointerDown: (e: React.PointerEvent, itemId: string) => void;
  handlePointerMove: (e: React.PointerEvent, itemId: string) => void;
  handlePointerUp: (e: React.PointerEvent, itemId: string) => void;
  handleBackpackClick: () => void;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useBoardingPassGame({ onVerified, onError }: GameProps): GameState {

  // ── Core game state ───────────────────────────────────────────────────── //
  const [phase,        setPhase]        = useState<Phase>('puzzle');
  const [shakenItem,   setShakenItem]   = useState<string | null>(null);
  const [wrongMsg,     setWrongMsg]     = useState<string | null>(null);
  const [isDragOver,   setIsDragOver]   = useState(false);
  const [backpackGlow, setBackpackGlow] = useState(false);
  const [draggedId,    setDraggedId]    = useState<string | null>(null);
  const [selectedId,   setSelectedId]   = useState<string | null>(null);

  // Ref + display state for packed items so handlers always see latest value
  // without needing to re-create themselves on every render.
  const [packedDisplay, setPackedDisplay] = useState<Set<string>>(new Set());
  const packedRef = useRef<Set<string>>(new Set());

  const setPacked = useCallback((next: Set<string>) => {
    packedRef.current = next;
    setPackedDisplay(new Set(next));
  }, []);

  // ── Internal refs (not exposed to components) ─────────────────────────── //
  const dropZoneRef  = useRef<HTMLDivElement | null>(null);
  const ghostRef     = useRef<HTMLDivElement | null>(null);
  const offsetRef    = useRef({ x: 0, y: 0 });
  const movedRef     = useRef(false);
  const activeIdRef  = useRef<string | null>(null);

  // ── Turnstile (frozen Phase 2 interface) ──────────────────────────────── //
  const turnstile = useTurnstile();

  // ── Drop logic ────────────────────────────────────────────────────────── //
  const handleDrop = useCallback((itemId: string) => {
    const current = packedRef.current;
    const item = ALL_ITEMS.find(i => i.id === itemId);
    if (!item || current.has(itemId)) return;

    if (item.correct) {
      const next = new Set(current);
      next.add(itemId);
      setPacked(next);
      setBackpackGlow(true);
      setTimeout(() => setBackpackGlow(false), 800);
      if (next.size === CORRECT_ITEMS.length) {
        setTimeout(() => setPhase('success'), 400);
      }
    } else {
      setShakenItem(itemId);
      setWrongMsg("That isn't essential for this journey.");
      setTimeout(() => setShakenItem(null), 700);
      setTimeout(() => setWrongMsg(null), 1500);
    }
  }, [setPacked]);

  // ── Pointer drag helpers ──────────────────────────────────────────────── //
  const cleanupDrag = useCallback(() => {
    ghostRef.current?.remove();
    ghostRef.current = null;
    activeIdRef.current = null;
    setDraggedId(null);
    setIsDragOver(false);
    movedRef.current = false;
  }, []);

  const isOverDropZone = useCallback((clientX: number, clientY: number) => {
    if (!dropZoneRef.current) return false;
    const r = dropZoneRef.current.getBoundingClientRect();
    return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  }, []);

  // ── Pointer event handlers (attached to each item card) ───────────────── //
  const handlePointerDown = useCallback((e: React.PointerEvent, itemId: string) => {
    if (packedRef.current.has(itemId) || phase !== 'puzzle') return;
    e.preventDefault();
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ignore */ }

    activeIdRef.current = itemId;
    setDraggedId(itemId);
    movedRef.current = false;

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    const item = ALL_ITEMS.find(i => i.id === itemId)!;
    const ghost = document.createElement('div');
    ghost.setAttribute('data-wr-ghost', '1');
    ghost.style.cssText = [
      'position:fixed',
      `left:${rect.left}px`, `top:${rect.top}px`,
      `width:${rect.width}px`, `height:${rect.height}px`,
      'z-index:99999', 'pointer-events:none',
      'border-radius:8px', 'overflow:hidden',
      item.correct
        ? 'background:linear-gradient(160deg,#0d1a38,#08112a)'
        : 'background:linear-gradient(160deg,#1e0808,#120404)',
      item.correct
        ? 'border:2px solid rgba(59,130,246,0.8)'
        : 'border:2px solid rgba(239,68,68,0.6)',
      'box-shadow:0 16px 48px rgba(0,0,0,0.75),0 0 30px rgba(59,130,246,0.35)',
      'display:flex', 'flex-direction:column', 'align-items:center',
      'transform:rotate(3deg) scale(1.06)', 'transition:none',
      'user-select:none', 'touch-action:none',
    ].join(';');
    ghost.innerHTML = `
      <img src="${item.imgUrl}" alt="${item.label}"
        style="width:100%;height:75%;object-fit:cover;display:block;"
        onerror="this.style.display='none';" />
      <div style="width:100%;padding:3px 0 4px;text-align:center;font-family:monospace;font-size:8.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:rgba(140,170,220,0.8);">${item.label}</div>
    `;
    document.body.appendChild(ghost);
    ghostRef.current = ghost;
  }, [phase]);

  const handlePointerMove = useCallback((e: React.PointerEvent, itemId: string) => {
    if (activeIdRef.current !== itemId) return;
    e.preventDefault();
    movedRef.current = true;
    if (ghostRef.current) {
      ghostRef.current.style.left = `${e.clientX - offsetRef.current.x}px`;
      ghostRef.current.style.top  = `${e.clientY - offsetRef.current.y}px`;
    }
    setIsDragOver(isOverDropZone(e.clientX, e.clientY));
  }, [isOverDropZone]);

  const handlePointerUp = useCallback((e: React.PointerEvent, itemId: string) => {
    if (activeIdRef.current !== itemId) return;
    const overDZ  = isOverDropZone(e.clientX, e.clientY);
    const wasTap  = !movedRef.current;
    cleanupDrag();

    if (wasTap) {
      // Tap-to-select (two-step UX): toggle selection, then tap backpack to pack
      setSelectedId(prev => prev === itemId ? null : itemId);
      return;
    }
    if (overDZ) handleDrop(itemId);
  }, [cleanupDrag, handleDrop, isOverDropZone]);

  // ── Tap-to-select: clicking the backpack packs the selected item ──────── //
  const handleBackpackClick = useCallback(() => {
    if (selectedId && phase === 'puzzle') {
      handleDrop(selectedId);
      setSelectedId(null);
    }
  }, [selectedId, phase, handleDrop]);

  // ── Trigger Turnstile after success animation ─────────────────────────── //
  useEffect(() => {
    if (phase !== 'success') return;
    const t = setTimeout(async () => {
      setPhase('verifying');

      // CLIENT-SIDE VERIFICATION TIMEOUT
      // Turnstile uses appearance:'interaction-only', meaning Cloudflare can
      // decide to present an interactive challenge (the "I am human" checkbox).
      // When that happens, the challenge renders inside a hidden container
      // (width:0, height:0, opacity:0). The user cannot see or interact with it,
      // so handleToken() is never called and execute() hangs indefinitely.
      //
      // Guard: if verification does not complete within 30 seconds, call onError
      // so the user gets actionable feedback rather than an infinite spinner.
      let timedOut = false;
      const timeoutHandle = setTimeout(() => {
        timedOut = true;
        onError(
          'Security check timed out. Please refresh the page and try again.',
        );
      }, 30_000);

      try {
        const token = await turnstile.execute();
        if (timedOut) return; // timeout already fired — discard late result
        await verifyTurnstileToken(token);
        if (timedOut) return;
        clearTimeout(timeoutHandle);
        onVerified();
      } catch (err) {
        if (timedOut) return; // timeout already fired — discard late error
        clearTimeout(timeoutHandle);
        const msg = err instanceof TurnstileVerificationError
          ? err.message
          : 'Security verification failed. Please refresh and try again.';
        onError(msg);
      }
    }, 1100);
    return () => clearTimeout(t);
  }, [phase, turnstile, onVerified, onError]);

  // ── Clean up ghost on unmount ─────────────────────────────────────────── //
  useEffect(() => () => { ghostRef.current?.remove(); }, []);

  return {
    phase,
    count: packedDisplay.size,
    packedDisplay,
    shakenItem,
    wrongMsg,
    isDragOver,
    backpackGlow,
    draggedId,
    selectedId,
    dropZoneRef,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleBackpackClick,
  };
}
